/*
 *  For the correct functionality of this library we use vite-plugin-node-polyfills
 *  https://www.npmjs.com/package/vite-plugin-node-polyfills
 *
 */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import crypto from 'crypto';
import { Readable, Transform } from 'stream-browserify';
import { tAxiosCustomConfig } from '../axios/axios.types';
import { AxiosService } from '../axios/axios.service';
import { createHeldBlobUrl, releaseBlobUrlHolder } from './blobUrlRegistry';

const AES_CHUNK_SIZE = 16;

type tLegacyAttachmentRequest = {
  url?: string;
  decryption?: tDescription;
  mimeType?: string;
  dataUrl?: string;
};
type tLegacyChat = {
  users: number;
  active: boolean;
  holder: object;
  downloads: Map<string, Promise<string | null>>;
};
const chats = new Map<string, tLegacyChat>();

export const retainLegacyChat = (chatId: string | undefined): (() => void) | undefined => {
  if (!chatId) return;
  let chat = chats.get(chatId);
  if (!chat) {
    chat = { users: 0, active: true, holder: {}, downloads: new Map() };
    chats.set(chatId, chat);
  }
  chat.users += 1;
  const retained = chat;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    retained.users -= 1;
    if (retained.users) return;
    retained.active = false;
    retained.downloads.clear();
    releaseBlobUrlHolder(retained.holder);
    if (chats.get(chatId) === retained) chats.delete(chatId);
  };
};

/**
 * Type definition for decryption keys.
 */
type tDescription = {
  keys: {
    iv: string;
    enc: string;
  };
};

/**
 * Type definition for message media attachment.
 */
export type MessageMediaAttachment = {
  url: string;
  decryption: {
    keys: {
      iv: string;
      enc: string;
    };
    algorithm: string;
  };
};

/**
 * Converts an ArrayBuffer to a Buffer.
 * @param {ArrayBuffer} ab - The ArrayBuffer to convert.
 * @returns {Buffer} - The converted Buffer.
 */
const toBuffer = (ab: ArrayBuffer): Buffer => {
  const buf = new Buffer(ab.byteLength);
  const view = new Uint8Array(ab);
  for (let i = 0; i < buf.length; ++i) {
    buf[i] = view[i];
  }
  return buf;
};

/**
 * Downloads an encrypted attachment from a given URL.
 * @param {string} url - The URL of the encrypted attachment.
 * @returns {Promise<Readable>} - A promise that resolves to a Readable stream of the encrypted attachment.
 */
const downloadEncryptedAttachment = async (url: string): Promise<Readable> => {
  const config: tAxiosCustomConfig = {
    responseType: 'arraybuffer',
    headers: {},
    isLegacy: true,
    isPublicAsset: true,
  };

  const response = await AxiosService.get(url, config);

  const buffer = toBuffer(response);
  const readable = new Readable();
  readable._read = () => {};
  readable.push(buffer);
  readable.push(null);

  return readable;
};

export const downloadMessageAttachment = async (
  request: tLegacyAttachmentRequest,
  chatId: string | undefined,
): Promise<string | null> => {
  const { url, decryption, dataUrl } = request;
  const chat = chatId ? chats.get(chatId) : undefined;
  if (
    !chat?.active ||
    decryption === null ||
    decryption === undefined ||
    dataUrl !== url ||
    !url?.includes('mmg.whatsapp.net')
  ) {
    return null;
  }

  const key = JSON.stringify([url, decryption.keys.iv, decryption.keys.enc, request.mimeType]);
  const cached = chat.downloads.get(key);
  if (cached) return cached;
  const download = decryptMessageAttachment(request).then((blob) =>
    chat.active ? createHeldBlobUrl(blob, chat.holder) : null,
  ).catch((error) => {
    chat.downloads.delete(key);
    throw error;
  });
  chat.downloads.set(key, download);
  return download;
};

const decryptMessageAttachment = async ({ url, decryption, mimeType }: tLegacyAttachmentRequest): Promise<Blob> => {

  const newUrl = url.replace(
    'https://mmg.whatsapp.net/',
    'whatsapp-web/media/mmg/',
  );

  const encrypted = await downloadEncryptedAttachment(`${newUrl}`);
  const stream = getDecryptedStream(encrypted, {
    iv: Buffer.from(decryption.keys.iv, 'base64'),
    cipherKey: Buffer.from(decryption.keys.enc, 'base64'),
  });

  let decrypted = Buffer.from([]);
  for await (const chunk of stream) {
    decrypted = Buffer.concat([decrypted, chunk]);
  }
  return new Blob([decrypted.buffer], { type: mimeType });
};

/**
 * Decrypts a stream using AES-256-CBC.
 * @param {Readable} stream - The encrypted stream.
 * @param {Object} options - The decryption options.
 * @param {Buffer} options.cipherKey - The cipher key.
 * @param {Buffer} options.iv - The initialization vector.
 * @returns {Transform} - A Transform stream that decrypts the input stream.
 */
const getDecryptedStream = (
  stream: Readable,
  { cipherKey, iv }: { cipherKey: Buffer; iv: Buffer },
): Transform => {
  const aes = crypto.createDecipheriv('aes-256-cbc', cipherKey, iv);

  let remainingBytes = Buffer.from([]);
  const output = new Transform({
    transform(chunk, _, callback) {
      let data = Buffer.concat([remainingBytes, chunk]);
      const decryptLength =
        Math.floor(data.length / AES_CHUNK_SIZE) * AES_CHUNK_SIZE;
      remainingBytes = data.slice(decryptLength);
      data = data.slice(0, decryptLength);

      try {
        this.push(aes.update(data));
        callback();
      } catch (error) {
        callback(error as Error);
      }
    },
    final(callback) {
      try {
        this.push(aes.final());
        callback();
      } catch (error) {
        callback(error as Error);
      }
    },
  });

  return stream.pipe(output, { end: true });
};
